import { useState, useCallback, useEffect, useRef } from "react";
import { setActivityCallback, setLogoutCallback, apiService } from "../services/apiService";
import * as tokenStore from "../services/tokenStore";
import { revoke, buildLogoutUrl } from "../services/oauthService";
import { isTransientAuthError } from "../services/authErrors";
import { clearHeapFileCache } from "../../simulation/heaps/compute/heapFileCache";

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes — mirrors the refresh-token idle window
const WARNING_MS    = 25 * 60 * 1000; // show warning at 25 min (5 min to go)
const COUNTDOWN_S   = (INACTIVITY_MS - WARNING_MS) / 1000; // seconds shown in the modal
const REFRESH_LEAD_MS = 60 * 1000;    // proactively refresh 1 min before expiry
const STORAGE_KEY   = "userData";     // {userId, user} — NEVER the token
const ACTIVITY_KEY  = "lastActivity";
// Session-restore retries while the server is unreachable (~60 s in total).
const RESTORE_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 15000, 15000];

export const UserAuth = () => {
  const [token, setToken] = useState();
  const [UID, setUID]     = useState();
  const [user, setUser]   = useState(null);
  // False until the mount-time session restore (refresh grant) settles —
  // App.js gates the route tree on this to avoid an AuthPage flash.
  const [authReady, setAuthReady] = useState(false);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(COUNTDOWN_S);

  const warningTimerRef  = useRef(null);
  const countdownRef     = useRef(null);
  const refreshTimerRef  = useRef(null);
  const tokenRef         = useRef(token);
  const warningVisibleRef = useRef(showInactivityWarning);

  const clearTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownRef.current);
    clearTimeout(refreshTimerRef.current);
    warningTimerRef.current = null;
    countdownRef.current    = null;
    refreshTimerRef.current = null;
  }, []);

  /**
   * Local sign-out cleanup. options.endServerSession additionally revokes
   * the refresh token and navigates to the Maestro AS logout page so the
   * login cookie dies too (explicit user logout only — inactivity and
   * cross-tab logouts stay local).
   */
  const logout = useCallback((options = {}) => {
    const { endServerSession = false, broadcast = true } = options;
    const refreshTokenValue = localStorage.getItem("auth.refreshToken");

    clearTimers();
    setToken(null);
    setUID(null);
    setUser(null);
    setShowInactivityWarning(false);
    setCountdownSeconds(COUNTDOWN_S);
    tokenStore.clearSession();
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ACTIVITY_KEY);
    localStorage.removeItem("selectedProject");
    localStorage.removeItem("selectedScenario");
    clearHeapFileCache(); // cached heap files are client data — fire-and-forget
    setActivityCallback(null);
    setLogoutCallback(null);
    if (broadcast) tokenStore.broadcastLogout();

    if (endServerSession) {
      revoke(refreshTokenValue); // best-effort, fire-and-forget
      window.location.assign(buildLogoutUrl(`${window.location.origin}/auth`));
    }
  }, [clearTimers]);

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownRef.current);
    setShowInactivityWarning(false);
    setCountdownSeconds(COUNTDOWN_S);
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
    // Warn the user 5 minutes before the idle window closes. Pure
    // client-side countdown — the server mirror is the refresh token's
    // 30-minute idle expiry.
    warningTimerRef.current = setTimeout(() => {
      let remaining = COUNTDOWN_S;
      setShowInactivityWarning(true);
      setCountdownSeconds(remaining);
      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setCountdownSeconds(remaining);
        if (remaining <= 0) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          logout();
        }
      }, 1000);
    }, WARNING_MS);
  }, [logout]);

  const login = useCallback((uid, tok, _expiration, userData = null) => {
    setToken(tok);
    setUID(uid);
    setUser(userData);
    // Identity only — tokens live in tokenStore (access: memory, refresh:
    // its own localStorage key).
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId: uid, user: userData }));
    setActivityCallback(resetInactivityTimer);
    setLogoutCallback(logout);
    resetInactivityTimer();
  }, [resetInactivityTimer, logout]);

  // Keep the API callback reference current after each render.
  useEffect(() => {
    if (token) setActivityCallback(resetInactivityTimer);
  }, [token, resetInactivityTimer]);

  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { warningVisibleRef.current = showInactivityWarning; }, [showInactivityWarning]);

  // Proactive refresh: renew the access token ~1 min before expiry, but only
  // while the user is active — an idle session must NOT be kept alive (the
  // server-side idle window is the policy).
  useEffect(() => {
    if (!token) return;
    const delay = Math.max(tokenStore.getExpiresAt() - Date.now() - REFRESH_LEAD_MS, 5000);
    refreshTimerRef.current = setTimeout(async () => {
      const lastActivity = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10);
      if (Date.now() - lastActivity >= WARNING_MS) return; // idle — let it lapse
      try {
        const fresh = await tokenStore.refresh();
        setToken(fresh); // re-arms this effect for the next cycle
      } catch (err) {
        if (err?.code === "invalid_grant") logout();
        // network errors: the 401-retry path in apiService picks it up later
      }
    }, delay);
    return () => clearTimeout(refreshTimerRef.current);
  }, [token, logout]);

  // Adopt auth events: token rotations (local or from other tabs), a sign-in
  // completed by another tab, and cross-tab logout.
  //
  // A bare setToken() on a signed-out tab is never allowed: App.js switches
  // to the signed-in route tree on the token alone, and with UID/user still
  // null the header shows no name or photo, useUserProfile never backfills
  // (no UID), and the sidebar's permission fetch bails — every
  // permission-gated section disappears. That is exactly the state a phone
  // landed in when the callback tab finished the exchange itself.
  useEffect(() => {
    let cancelled = false;
    const hydrateFromOtherTab = async (accessToken) => {
      try {
        // The signing-in tab persists identity in login(); it may not have
        // written it yet when its token broadcast arrives, so fall back to
        // the profile endpoint.
        let stored = null;
        try { stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { /* corrupt */ }
        if (stored?.userId && stored?.user) {
          if (!cancelled) login(stored.userId, accessToken, null, stored.user);
          return;
        }
        const data = await apiService.get("/v1/auth/profile", accessToken);
        const profile = data.user || data;
        if (!cancelled) login(profile.id || profile._id, accessToken, null, profile);
      } catch {
        // Stay signed out — the next sign-in or mount restore recovers.
      }
    };
    const unsubscribe = tokenStore.onAuthEvent((event) => {
      if (event.type === "token") {
        if (tokenRef.current) {
          setToken(event.accessToken); // rotation of a live session
        } else if (event.remote) {
          hydrateFromOtherTab(event.accessToken);
        }
        // Local first token: the sign-in / restore caller follows up with
        // login(profile) itself — nothing to adopt here.
      } else if (event.type === "logout") {
        logout({ broadcast: false }); // don't echo
      }
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [login, logout]);

  // Reset inactivity timer on any meaningful user interaction.
  useEffect(() => {
    if (!token) return;
    const EVENTS = ["mousedown", "keydown", "scroll", "touchstart"];
    let debounce = null;
    const handleActivity = () => {
      // While warning is visible, require explicit user confirmation.
      if (warningVisibleRef.current) return;
      clearTimeout(debounce);
      debounce = setTimeout(resetInactivityTimer, 500);
    };
    EVENTS.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    return () => {
      clearTimeout(debounce);
      EVENTS.forEach(e => window.removeEventListener(e, handleActivity));
    };
  }, [token, resetInactivityTimer]);

  // On focus/visibility return: idle past the window → logout; otherwise
  // refresh if the access token lapsed while backgrounded.
  useEffect(() => {
    if (!token) return;
    const handleWindowReturn = () => {
      if (document.visibilityState === "hidden") return;
      const lastActivity = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10);
      if (Date.now() - lastActivity >= INACTIVITY_MS) {
        logout();
        return;
      }
      if (tokenStore.isExpired()) {
        tokenStore.refresh()
          .then((fresh) => setToken(fresh))
          .catch((err) => { if (err?.code === "invalid_grant") logout(); });
      }
    };
    document.addEventListener("visibilitychange", handleWindowReturn);
    window.addEventListener("focus", handleWindowReturn);
    return () => {
      document.removeEventListener("visibilitychange", handleWindowReturn);
      window.removeEventListener("focus", handleWindowReturn);
    };
  }, [token, logout]);

  // Session restore on mount: a refresh token + recent activity → silent
  // refresh grant, then load the profile. No stored access token exists.
  //
  // An unreachable server (network / 5xx / 429 — Maestro restarting) is NOT a
  // dead session: keep retrying behind the boot screen for about a minute, and
  // if it is still down, show the signed-out app but KEEP the refresh token so
  // the next load restores. Only a rejected session clears it. (Clearing on
  // any failure is how a reload during a ~30 s Maestro restart signed users
  // out for good on 2026-10-26.)
  useEffect(() => {
    let cancelled = false;
    const clearStored = () => {
      tokenStore.clearSession();
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ACTIVITY_KEY);
    };
    (async () => {
      try {
        const lastActivity = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10);
        if (!tokenStore.hasRefreshToken() || Date.now() - lastActivity >= INACTIVITY_MS) {
          clearStored();
          return;
        }
        for (let attempt = 0; ; attempt += 1) {
          try {
            const accessToken = await tokenStore.refresh();
            const data = await apiService.get("/v1/auth/profile", accessToken);
            const profile = data.user || data;
            if (!cancelled) {
              login(profile.id || profile._id, accessToken, null, profile);
            }
            return;
          } catch (err) {
            if (cancelled) return;
            if (!isTransientAuthError(err)) {
              clearStored();
              return;
            }
            if (attempt >= RESTORE_RETRY_DELAYS_MS.length) return; // keep the refresh token
            await new Promise((r) => setTimeout(r, RESTORE_RETRY_DELAYS_MS[attempt]));
            if (cancelled) return;
          }
        }
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [login]);

  // Cleanup on unmount.
  useEffect(() => () => clearTimers(), [clearTimers]);

  // "Continue Session" in the expiry modal: rotate the refresh token. If the
  // server is only unreachable, the user is clearly present — dismiss the
  // warning and keep the session; the next API call's 401 path refreshes.
  const refreshSession = useCallback(async () => {
    try {
      const fresh = await tokenStore.refresh();
      setToken(fresh);
      resetInactivityTimer();
    } catch (err) {
      if (isTransientAuthError(err)) resetInactivityTimer();
      else logout();
    }
  }, [resetInactivityTimer, logout]);

  return { token, login, logout, UID, user, authReady, showInactivityWarning, countdownSeconds, resetInactivityTimer, refreshSession };
};
